import { useState, useRef } from "react"
import styles from "./FeedCard.module.scss"
import { connect } from "react-redux"
import {
  likePost,
  createPostComment,
  deletePost,
  openMainModal,
  editPosteo,
} from "../../../store/actions"
import { ReactComponent as MundoPost } from "../../../assets/svg/worldPost.svg"
import { ReactComponent as DotPost } from "../../../assets/svg/dotPost.svg"
import { ReactComponent as PencilIcon } from "../../../assets/svg/editComment.svg"
import { useNavigate } from "react-router-dom"
import TextareaAutosize from "react-textarea-autosize"
import useUserImage from "../../../hooks/useUserImage"
import { ReactComponent as NoImage } from "../../../assets/svg/noImageIcon.svg"
import FeedCardComment from "./FeedCardComment.tsx"
import FeedCardUsersReacted from "./FeedCardUsersReacted.tsx"
import { Check, X, Heart, MessageSquare, ChevronDown } from "lucide-react"
import { useActionGuard } from "../../../hooks/useActionGuard.ts"
import type { FeedPost } from "../../../types/feed.ts"

interface FeedCardProps {
  userData: FeedPost["user"] & { id?: number | string }
  likePost: (postId: number) => void
  createPostComment: (postId: number, content: string) => void
  deletePost: (id: number) => void
  openMainModal: (config: {
    title: string
    body: string
    isSuccess: boolean
    iconType?: string
    confirmHandler: () => void
  }) => void
  editPosteo: (body: {
    id: number | string
    images?: unknown
    content?: string
    video?: unknown
  }) => void
  myPosts: boolean
  item: FeedPost
}

const FeedCard: React.FC<FeedCardProps> = ({
  userData,
  likePost,
  createPostComment,
  deletePost,
  openMainModal,
  editPosteo,
  myPosts,
  item,
}) => {
  const { guard } = useActionGuard()
  const [isEditingPost, setIsEditingPost] = useState(false)
  const [editPost, setEditPost] = useState("")
  const [editImage, setEditImage] = useState<File | "">("")
  const [editImagePreview, setEditImagePreview] = useState("")
  const [editVideo, setEditVideo] = useState<File | "">("")
  const [editVideoPreview, setEditVideoPreview] = useState("")
  const [showMenu, setShowMenu] = useState(false)
  const [showUsersReacted, setShowUsersReacted] = useState(false)
  const [showComments, setShowComments] = useState<number | false>(false)
  const [heartAnimation, setHeartAnimation] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  const [liking, setLiking] = useState(false)

  const navigate = useNavigate()
  const cardRef = useRef<HTMLElement | null>(null)

  const { content, id, createdAt, video, images } = item
  const comments = item.comments ?? []
  const commentsCount =
    typeof item.commentsCount === "number"
      ? item.commentsCount
      : typeof item.comments_count === "number"
        ? item.comments_count
        : comments.length
  const { name, last_name, razon_social, type, id: userId } = item.user ?? {}
  const likesArray = item.likes ?? []
  const isMyLike = likesArray.some(
    (like) => like?.user?.id != null && userData?.id != null && String(like.user.id) === String(userData.id)
  )
  const isMyPost =
    item?.user?.id != null && userData?.id != null && String(item.user.id) === String(userData.id)
  const hasImage = Boolean(item.images?.[0]?.image)
  const hasVideo = Boolean(item.video?.[0]?.video)
  const completeName =
    type === "COMPANY" ? (razon_social ?? "") : `${name ?? ""} ${last_name ?? ""}`.trim()
  const { imageError, initials, profileImage } = useUserImage(item.user)

  const handleProfile = (profileId: number | string, completeName: string) => {
    navigate(`/perfil/${profileId}`)
  }

  const handleLike = guard("like_post", (postId: number) => {
    setHeartAnimation(true)
    setLiking(true)
    const p = likePost(postId) as unknown as Promise<unknown> | undefined
    if (p && typeof p.finally === "function") p.finally(() => setLiking(false))
    else setLiking(false)
    setTimeout(() => setHeartAnimation(false), 2000)
  })

  const calcularDiferenciaTiempo = (fechaCreacion: string): string => {
    const fechaActual = new Date()
    const fechaCreacionPost = new Date(fechaCreacion)
    const diferencia = fechaActual.getTime() - fechaCreacionPost.getTime()

    const minutos = Math.floor(diferencia / (1000 * 60))
    const horas = Math.floor(diferencia / (1000 * 60 * 60))
    const dias = Math.floor(diferencia / (1000 * 60 * 60 * 24))
    const semanas = Math.floor(dias / 7)
    const meses = Math.floor(dias / 30)

    if (minutos === 0) return "Ahora"
    if (minutos === 1) return "1 minuto"
    if (minutos < 60) return `${minutos} minutos`
    if (horas === 1) return "1 hora"
    if (horas < 24) return `${horas} horas`
    if (dias === 1) return "1 día"
    if (dias < 7) return `${dias} días`
    if (dias < 30) {
      if (semanas === 1) return "1 semana"
      return `${semanas} semanas`
    }
    if (meses === 1) return "1 mes"
    return `${meses} meses`
  }

  const handleShowPostComments = (postId: number) => {
    const currentScrollY = window.scrollY
    const cardTop = cardRef.current?.offsetTop ?? 0

    if (showComments === postId) {
      setShowComments(false)
    } else {
      setShowComments(postId)

      setTimeout(() => {
        const newCardTop = cardRef.current?.offsetTop ?? 0
        const scrollDifference = newCardTop - cardTop
        window.scrollTo(0, currentScrollY + scrollDifference)
      }, 100)
    }
  }

  const handleToggleMenu = () => {
    setShowMenu(!showMenu)
  }

  const handleDeletePost = (postId: number) => {
    openMainModal({
      title: "¿Estas seguro de querer eliminar el posteo?",
      body: "Si lo eliminas no podrás recuperarlo",
      isSuccess: false,
      iconType: "warning",
      confirmHandler: () => deletePost(postId),
    })
  }

  const handleEditPost = (
    postId: number,
    images: File | unknown,
    content: string,
    video: File | unknown
  ) => {
    const body = { id: postId, images, content, video }
    editPosteo(body)
    setIsEditingPost(false)
    setEditPost("")
    setEditImage("")
    setEditImagePreview("")
    setEditVideo("")
    setEditVideoPreview("")
  }

  const handleCancelEditPost = () => {
    setIsEditingPost(false)
    setEditPost("")
    setEditImage("")
    setEditImagePreview("")
    setEditVideo("")
    setEditVideoPreview("")
    setShowMenu(false)
  }

  const handleMenuItemClick = (
    action: string,
    postId: number
  ) => {
    setShowMenu(false)
    if (action === "Eliminar") {
      handleDeletePost(postId)
    }
    if (action === "Editar") {
      setIsEditingPost(true)
      setEditPost(content ?? "")
    }
    if (action === "Cerrar") {
      setIsEditingPost(false)
    }
  }

  return (
    <section className={styles.generalContainer} ref={cardRef}>
      <div
        className={`${styles.feed_itemContainer} ${isHovered ? styles.hovered : ""}`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className={styles.feed_itemHeader_profileContainer}>
          <div className={styles.feed_itemHeader_profileContainer_nameAndImg}>
            <div
              className={styles.feed_itemHeader_imgContainer}
              onClick={() => handleProfile(userId ?? id, completeName)}
            >
              {!imageError && profileImage ? (
                <img
                  className={styles.feed_itemHeader_imgContainer_img}
                  src={profileImage || "/placeholder.svg"}
                  alt="user"
                />
              ) : initials ? (
                <div className={styles.feed_itemHeader_imgContainer_initials}>
                  {initials}
                </div>
              ) : (
                <div className={styles.feed_itemHeader_imgContainer_img}>
                  <NoImage width={60} height={60} />
                </div>
              )}
            </div>
            <div
              className={styles.feed_itemHeader_text}
              onClick={() => handleProfile(userId ?? id, completeName)}
            >
              <div className={styles.feed_itemHeader_info}>
                <p className={styles.feed_itemHeader_text_name}>{completeName}</p>
                <div className={styles.feed_itemHeader_text_timeContainer}>
                  <MundoPost width={15} height={15} />
                  <span className={styles.feed_itemHeader_text_time}>
                    <DotPost height={10} width={10} />
                  </span>
                  {calcularDiferenciaTiempo(createdAt)}
                </div>
              </div>
            </div>
          </div>
        </div>
        {hasImage && isEditingPost && (
          editImagePreview ? (
            <div className={styles.feed_itemBody_imgContainer}>
              <img
                src={editImagePreview || "/placeholder.svg"}
                alt=""
                className={styles.feed_itemBody_imgPost}
              />
            </div>
          ) : (
            <label htmlFor="postImage" className={styles.feed_itemBody_input_item_label}>
              <input
                type="file"
                accept="image/*"
                id="postImage"
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  if (file) {
                    setEditImagePreview(URL.createObjectURL(file))
                    setEditImage(file)
                  }
                }}
                style={{ display: "none" }}
              />
              <div className={styles.feed_itemBody_imgContainer}>
                <img
                  src={images?.[0]?.image || "/placeholder.svg"}
                  alt=""
                  className={styles.feed_itemBody_imgPost}
                />
                <p className={styles.feed_itemBody_imgContainer_editButton}>
                  Cambiar imagen
                </p>
              </div>
            </label>
          )
        )}
        {hasImage && !isEditingPost && (
          <div
            className={styles.feed_itemBody_imgContainer}
            style={{ "--bg-image": `url(${images?.[0]?.image})` } as React.CSSProperties}
          >
            <img
              src={images?.[0]?.image || "/placeholder.svg"}
              alt={images?.[0]?.image}
              className={styles.feed_itemBody_imgPost}
            />
          </div>
        )}
        {hasVideo && isEditingPost && (
          editVideoPreview ? (
            <div className={styles.feed_itemBody_videoContainer}>
              <video
                src={editVideoPreview}
                controls
                className={styles.feed_itemBody_videoPost}
              />
            </div>
          ) : (
            <label htmlFor="postVideo" className={styles.feed_itemBody_input_item_label}>
              <input
                type="file"
                accept="video/*"
                id="postVideo"
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  if (file) {
                    setEditVideoPreview(URL.createObjectURL(file))
                    setEditVideo(file)
                  }
                }}
                style={{ display: "none" }}
              />
              <div className={styles.feed_itemBody_videoContainer}>
                <video
                  src={video?.[0]?.video}
                  controls
                  className={styles.feed_itemBody_videoPost}
                />
                <p className={styles.feed_itemBody_videoContainer_editButton}>
                  Cambiar video
                </p>
              </div>
            </label>
          )
        )}
        {hasVideo && !isEditingPost && (
          <div
            className={styles.feed_itemBody_videoContainer}
            style={{ backgroundColor: "rgb(0 0 0 / 80%)" }}
          >
            <video
              src={video?.[0]?.video}
              controls
              className={styles.feed_itemBody_videoPost}
            />
          </div>
        )}

        {isEditingPost ? (
          <div className={styles.feed_itemBody_inputContainer}>
            <TextareaAutosize
              className={styles.feed_itemBody_input_item}
              id="postText"
              minRows={3}
              value={(editPost || content) ?? ""}
              onChange={(e) => setEditPost(e.target.value)}
              onInput={(e) => {
                const target = e.target as HTMLTextAreaElement
                target.style.height = "auto"
                target.style.height = target.scrollHeight + 10 + "px"
              }}
            />
            <div className={styles.feed_itemBody_input_item_iconContainer}>
              <button
                className={styles.feed_itemBody_input_item_iconContainer_cancelBtn}
                onClick={() => handleCancelEditPost()}
              >
                <X size={16} />
                Cancelar
              </button>
              <button
                className={styles.feed_itemBody_input_item_iconContainer_sendBtn}
                onClick={() =>
                  handleEditPost(id, editImage, editPost, editVideo)
                }
              >
                <Check size={16} />
                Guardar
              </button>
            </div>
          </div>
        ) : (
          content && <div className={styles.feed_itemBody_txt}>{content}</div>
        )}
        <div className={styles.feed_itemHeader_footerContainer}>
          <div className={styles.feed_itemHeader_actionsMenu_menu}>
            <div
              className={[
                styles.feed_itemHeader_actionsMenu_menu_item,
                heartAnimation && styles.heartbeat,
                styles.heartIcon,
                isMyLike && styles.heartIcon_active,
                liking && styles.feed_itemHeader_actionsMenu_menu_item_loading,
              ]
                .filter(Boolean)
                .join(" ")}
              onClick={() => !liking && handleLike(id)}
              onMouseEnter={() => setShowUsersReacted(true)}
              onMouseLeave={() => setShowUsersReacted(false)}
            >
              <Heart size={20} />
              {likesArray?.length > 0 && (
                <p className={styles.feed_itemHeader_actionsMenu_menu_item_number}>
                  {likesArray.length}
                </p>
              )}
              {showUsersReacted && (
                <FeedCardUsersReacted
                  users={likesArray}
                  handleProfile={handleProfile}
                />
              )}
            </div>
            <div
              className={styles.feed_itemHeader_actionsMenu_menu_item}
              onClick={() => handleShowPostComments(id)}
            >
              <MessageSquare size={20} aria-label="Comentar" />
              {commentsCount > 0 && (
                <p className={styles.feed_itemHeader_actionsMenu_menu_item_number}>
                  {commentsCount}
                </p>
              )}
              <span className={styles.feed_itemHeader_actionsMenu_menu_item_text}>
                Comentarios
                <ChevronDown
                  size={16}
                  className={
                    showComments === id
                      ? styles.feed_itemHeader_actionsMenu_menu_item_iconRotated
                      : ""
                  }
                />
              </span>
            </div>
          </div>
          {isMyPost && (
            <div className={styles.feed_itemHeader_options}>
              <div
                className={styles.feed_itemHeader_options_container_item}
                onClick={() => handleToggleMenu()}
              >
                <PencilIcon width={16} height={16} title="Editar" />
              </div>
              {showMenu && (
                <ul className={styles.feed_itemHeader_options_dropdownMenu}>
                  <li
                    className={styles.feed_itemHeader_options_dropdownMenu_close}
                    onClick={() => handleToggleMenu()}
                  >
                    X
                  </li>
                  <li
                    className={styles.feed_itemHeader_options_dropdownMenu_item}
                    onClick={() => handleMenuItemClick("Editar", id)}
                  >
                    Editar
                  </li>
                  <li
                    className={styles.feed_itemHeader_options_dropdownMenu_item}
                    onClick={() => handleMenuItemClick("Eliminar", id)}
                  >
                    Eliminar
                  </li>
                </ul>
              )}
            </div>
          )}
        </div>
        <FeedCardComment
          comments={comments}
          userData={userData}
          showComments={showComments}
          setShowComments={setShowComments}
          id={id}
          isHovered={isHovered}
          calcularDiferenciaTiempo={calcularDiferenciaTiempo}
          commentsLoaded={true}
        />
      </div>
    </section>
  )
}

const mapStateToProps = (state: { general: { userData: FeedCardProps["userData"] } }) => ({
  userData: state.general.userData,
})

export default connect(mapStateToProps, {
  likePost,
  createPostComment,
  deletePost,
  openMainModal,
  editPosteo,
})(FeedCard)
